/**
 * 应用主入口
 * App Module - app.js
 * 负责路由、菜单切换、日期显示等核心功能
 */

import { initDb, isAvailable, showDbWarning } from './db.js'
import { checkAuth, initAuthUI, getCurrentUser } from './auth.js'

// 使用 Vite 的 import.meta.glob 预加载所有模块（懒加载模式）
const moduleLoaders = import.meta.glob('./modules/*.js')

// 使用 Vite 的 ?raw 导入，在构建时将 HTML 片段内联到 JS 中
// 这样生产环境不再需要运行时 fetch，彻底避免 404 问题
import inspirationHTML from '../pages/inspiration.html?raw'
import reviewHTML from '../pages/review.html?raw'
import transactionHTML from '../pages/transaction.html?raw'
import newsHTML from '../pages/news.html?raw'
import quoteHTML from '../pages/quote.html?raw'
import courseHTML from '../pages/course.html?raw'

// 使用 Vite 的 ?raw 导入，在构建时将模块 CSS 内联到 JS 中
import inspirationCSS from '../css/modules/inspiration.css?raw'
import reviewCSS from '../css/modules/review.css?raw'
import transactionCSS from '../css/modules/transaction.css?raw'
import newsCSS from '../css/modules/news.css?raw'
import quoteCSS from '../css/modules/quote.css?raw'
import courseCSS from '../css/modules/course.css?raw'

// 模块映射表（HTML 和 CSS 改为直接引用内联变量）
const MODULE_MAP = {
  inspiration: { html: inspirationHTML, css: inspirationCSS, module: 'inspiration.js', label: '灵感速记' },
  review: { html: reviewHTML, css: reviewCSS, module: 'review.js', label: '每日复盘' },
  transaction: { html: transactionHTML, css: transactionCSS, module: 'transaction.js', label: '收支记录' },
  news: { html: newsHTML, css: newsCSS, module: 'news.js', label: '新闻热点' },
  quote: { html: quoteHTML, css: quoteCSS, module: 'quote.js', label: '每日文摘' },
  course: { html: courseHTML, css: courseCSS, module: 'course.js', label: '技能学习' },
}

// 当前激活的模块
let currentModule = null

// 是否已完成工作台初始化
let appInitialized = false

/**
 * 应用初始化
 */
async function initApp() {
  console.log('[App] 应用初始化开始')

  // 1. 初始化数据库
  const dbOk = initDb()
  if (!dbOk) {
    console.log('[App] 数据库不可用，将使用降级模式')
  }

  // 2. 监听来自 auth.js 的登录成功事件（无论何时都先注册）
  document.addEventListener('auth:login-success', onLoginSuccess)

  // 3. 检查认证状态
  const isLoggedIn = await checkAuth()

  if (isLoggedIn) {
    activateApp()
  } else {
    showLogin()
  }

  // 4. 显示当前日期
  updateDateDisplay()

  // 5. 初始化菜单交互
  initMenu()

  // 6. 更新用户信息显示
  updateUserDisplay()

  console.log('[App] 应用初始化完成')
}

/**
 * 登录成功回调（auth.js 表单登录触发）
 */
function onLoginSuccess() {
  console.log('[App] 收到登录成功事件，激活工作台')
  activateApp()
}

/**
 * 激活工作台界面（统一入口）
 * 负责隐藏登录框、显示工作台、加载默认模块
 */
function activateApp() {
  const authContainer = document.getElementById('auth-container')
  const appContainer = document.getElementById('app-container')

  if (authContainer) authContainer.style.display = 'none'
  if (appContainer) appContainer.style.display = 'flex'

  // 只在首次激活时加载默认模块
  if (!appInitialized) {
    appInitialized = true
    onAppReady()
  }
}

/**
 * 显示登录界面
 */
function showLogin() {
  const authContainer = document.getElementById('auth-container')
  const appContainer = document.getElementById('app-container')

  if (authContainer) authContainer.style.display = 'flex'
  if (appContainer) appContainer.style.display = 'none'

  initAuthUI()
}

/**
 * 工作台就绪后的初始化
 */
function onAppReady() {
  // 在内容区显示数据库警告
  const contentArea = document.querySelector('.content-area')
  if (contentArea && showDbWarning(contentArea)) {
    console.log('[App] 已显示数据库连接提示')
  }

  // 默认加载第一个模块
  switchModule('inspiration')
}

/**
 * 初始化侧边栏菜单
 */
function initMenu() {
  const menuItems = document.querySelectorAll('.menu-item')

  menuItems.forEach(item => {
    item.addEventListener('click', () => {
      const moduleKey = item.dataset.module
      if (moduleKey) {
        switchModule(moduleKey)
      }
    })
  })
}

/**
 * 切换模块/页面
 * @param {string} moduleKey - 模块标识（如 'inspiration', 'review' 等）
 */
async function switchModule(moduleKey) {
  if (currentModule === moduleKey) return

  const moduleInfo = MODULE_MAP[moduleKey]
  if (!moduleInfo) {
    console.error('[App] 未知模块:', moduleKey)
    return
  }

  console.log('[App] 切换到模块:', moduleInfo.label)

  // 1. 更新菜单高亮状态
  updateMenuHighlight(moduleKey)

  // 2. 更新顶部栏标题
  updateTopbarTitle(moduleInfo.label)

  // 3. 注入模块 CSS（内联，不再 fetch）
  injectModuleCSS(moduleKey, moduleInfo.css)

  // 4. 注入页面 HTML（内联，不再 fetch）
  injectPageHTML(moduleInfo.html)

  // 5. 初始化模块 JS
  await initModuleJS(moduleInfo.module)

  currentModule = moduleKey
}

/**
 * 更新菜单高亮状态
 */
function updateMenuHighlight(moduleKey) {
  const menuItems = document.querySelectorAll('.menu-item')

  menuItems.forEach(item => {
    if (item.dataset.module === moduleKey) {
      item.classList.add('active')
    } else {
      item.classList.remove('active')
    }
  })
}

/**
 * 更新顶部栏标题
 */
function updateTopbarTitle(label) {
  const title = document.getElementById('topbar-current-title')
  if (title) {
    title.textContent = label
  }
}

/**
 * 注入模块 CSS（从内联变量直接创建 style 标签）
 */
function injectModuleCSS(moduleKey, cssText) {
  const cssId = `module-css-${moduleKey}`

  // 已加载则跳过
  if (document.getElementById(cssId)) return

  const style = document.createElement('style')
  style.id = cssId
  style.textContent = cssText
  document.head.appendChild(style)
}

/**
 * 注入页面 HTML（从内联变量直接写入 content-area）
 */
function injectPageHTML(htmlText) {
  const contentArea = document.querySelector('.content-area')
  if (!contentArea) return
  contentArea.innerHTML = htmlText
}

/**
 * 初始化模块 JS
 * 使用 import.meta.glob 预加载的模块映射进行懒加载
 */
async function initModuleJS(moduleFile) {
  try {
    const modulePath = `./modules/${moduleFile}`
    const loader = moduleLoaders[modulePath]

    if (loader) {
      const module = await loader()
      if (module && typeof module.init === 'function') {
        await module.init()
      }
    } else {
      console.warn(`[App] 未找到模块: ${moduleFile}`)
    }
  } catch (err) {
    console.error(`[App] 初始化模块失败: ${moduleFile}`, err)
  }
}

/**
 * 更新日期显示
 */
function updateDateDisplay() {
  const dateEl = document.getElementById('current-date')
  if (dateEl) {
    const now = new Date()
    const weekdays = ['日', '一', '二', '三', '四', '五', '六']
    const formatted = `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 周${weekdays[now.getDay()]}`
    dateEl.textContent = formatted
  }
}

/**
 * 更新用户信息显示
 */
function updateUserDisplay() {
  const user = getCurrentUser()

  const topbarAvatar = document.querySelector('.topbar-avatar')
  const sidebarName = document.querySelector('.user-name')
  const sidebarAvatar = document.querySelector('.user-avatar')

  if (user) {
    const initial = (user.email || 'U')[0].toUpperCase()

    if (topbarAvatar) topbarAvatar.textContent = initial
    if (sidebarAvatar) sidebarAvatar.textContent = initial
    if (sidebarName) sidebarName.textContent = user.email || '本地用户'
  }
}

// --- 页面加载完成后初始化 ---
document.addEventListener('DOMContentLoaded', initApp)
